import { Alert, Box, Flex, Group, Tabs } from "@mantine/core";
import { IMeeting } from "@mctypes/meeting.types";
import React, { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useGetProjectByProjectIdMeetingAndMeetingIdQuery } from "../../../../../openapi";
import { FailedAlert, McTitle } from "../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { MCApplicationsUrlEnum } from "@mcapps/shared/sharedConstants";
import { getProjectIdFromUrl } from "../../../utils";
import { doLogUsage } from "../../admin/restQueries";
import { MeetingTab, setMeetingActiveTab } from "../meetingSlice";
import { MeetingDetails } from "../meetingApiTypes";
import Agenda from "./Agenda/Agenda";
import MeetingInfo from "./Info/MeetingInfo";
import { MeetingDocx } from "./MeetingDocx";
import MeetingSeriesInstanceSelector from "./MeetingSeriesInstanceSelector";
import Participants from "./Participants/Participants";

const SingleMeeting: React.FC<{ meetingId: string }> = ({ meetingId }) => {
  const dispatch = useAppDispatch();

  const history = useHistory();
  const projectId = getProjectIdFromUrl(history.location.pathname);
  const {
    data: singleMeetingResponse,
    isLoading,
    isError,
  } = useGetProjectByProjectIdMeetingAndMeetingIdQuery({
    meetingId,
    projectId,
  });
  const singleMeeting: MeetingDetails | undefined = singleMeetingResponse;
  const singleMeetingActiveTab = useAppSelector((state) => state.meetingReducer.singleMeetingActiveTab);
  const project = useAppSelector((state) => state.adminReducer.project);

  const meetingSerie = singleMeeting?.meetingSerie as IMeeting.MeetingSerie;

  useEffect(() => {
    doLogUsage(project, MCApplicationsUrlEnum.MEETING, "open");

    const getActiveTabFromUrl = (): MeetingTab => {
      const queryParams = new URLSearchParams(history.location.search);
      const tab = queryParams.get("tab")?.toUpperCase() as MeetingTab | null;

      const validTabs: MeetingTab[] = ["AGENDA", "PARTICIPANTS", "INFO", "DOCX"];
      return tab && validTabs.includes(tab) ? tab : "AGENDA";
    };

    const activeTab = getActiveTabFromUrl();

    if (activeTab) {
      dispatch(setMeetingActiveTab(activeTab));
    }
  }, []);
  const handleSetActiveTab = (tab: MeetingTab) => {
    dispatch(setMeetingActiveTab(tab));
    history.push({
      search: `?tab=${tab.toLowerCase()}`,
    });
  };
  return (
    <Flex direction="column" h="100%" style={{ overflow: "auto" }} w="100%">
      {isError === true && <FailedAlert errorMessage="Failed to fetch meeting" />}
      {singleMeeting != null && (
        <>
          <Group
            justify="space-between"
            align="end"
            pt="xs"
            mx="xs"
            wrap="nowrap"
            style={{ boxShadow: "inset 0 -2px 0 0 #e4e7eb" }}
          >
            <Tabs
              value={singleMeetingActiveTab.toLowerCase()}
              onChange={(v) => v && handleSetActiveTab(v.toUpperCase() as any)}
              styles={{ tab: { fontWeight: 600 } }}
            >
              <Tabs.List>
                <Tabs.Tab style={{ fontSize: "1.05em", fontWeight: 400 }} value="info">
                  Info
                </Tabs.Tab>
                <Tabs.Tab style={{ fontSize: "1.05em", fontWeight: 400 }} value="agenda">
                  Møteinnhold
                </Tabs.Tab>
                <Tabs.Tab style={{ fontSize: "1.05em", fontWeight: 400 }} value="participants">
                  Møtedeltagere
                </Tabs.Tab>
                <Tabs.Tab style={{ fontSize: "1.05em", fontWeight: 400 }} value="docx">
                  Eksport
                </Tabs.Tab>
              </Tabs.List>
            </Tabs>
            <Group gap="sm" wrap="nowrap" pb="xs">
              {meetingSerie != null && (
                <McTitle.InlineData positionInlineData="before">
                  <Box component="h5" className="bold" pt="0.25rem">
                    Møte i serie:
                  </Box>
                  <MeetingSeriesInstanceSelector singleMeeting={singleMeeting} />
                </McTitle.InlineData>
              )}
            </Group>
          </Group>
          <div style={{ flexGrow: 1, overflow: "auto" }}>
            {singleMeetingActiveTab === "AGENDA" && <Agenda singleMeeting={singleMeeting} />}
            {singleMeetingActiveTab === "PARTICIPANTS" && <Participants singleMeeting={singleMeeting} />}
            {singleMeetingActiveTab === "INFO" && <MeetingInfo singleMeeting={singleMeeting} />}
            {singleMeetingActiveTab === "DOCX" && <MeetingDocx singleMeeting={singleMeeting} />}
          </div>
        </>
      )}
    </Flex>
  );
};

export default SingleMeeting;

export const BetaWarningBanner: React.FC = () => {
  return (
    <Alert color="yellow" variant="light">
      <h5>Beta-utgave: Mindre endringer og feil kan forekomme. Rapporter til mcapps@multiconsult.no.</h5>
    </Alert>
  );
};
